/**
 * Dev-only zoom debug buttons.
 *
 * Rendered from `MapComponent` alongside the FPS and compass debug
 * triggers when the `VITE_DEV_SHOW_ZOOM_BUTTONS` environment flag is
 * enabled. Clicking `+` or `-` asks the map to zoom in/out so developers
 * can drive the map without touch/scroll gestures on a desktop browser.
 *
 * Visual style matches the other debug triggers (small pill with muted
 * opacity in the bottom-right corner).
 */

import React, { useMemo } from "react";
import { getUIColorPalette } from "../../theme/ColorPalette";

/** Props exposed by the zoom debug buttons. */
export interface ZoomDebugButtonsProps {

    /** Zoom the map in by one step. */
    "onZoomIn": () => void;

    /** Zoom the map out by one step. */
    "onZoomOut": () => void;

    /** Apply dark palette variants when `true`. */
    "isDarkUi": boolean;
}

/**
 * Dev-only +/- zoom button pair.
 *
 * @param props Component props. See `ZoomDebugButtonsProps`.
 * @returns The rendered debug button pair.
 */
const ZoomDebugButtons: React.FC<ZoomDebugButtonsProps> = ( {
    onZoomIn,
    onZoomOut,
    isDarkUi,
} ) => {
    const palette = useMemo( () => getUIColorPalette( isDarkUi ), [ isDarkUi ] );

    return (
        <div
            aria-label="Zoom controls (dev only)"
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-1"
            role="group"
        >
            <button
                aria-label="Zoom in (dev only)"
                className="rounded-full px-2 py-1 text-[10px] border opacity-45 hover:opacity-80 active:opacity-90 transition-opacity"
                onClick={onZoomIn}
                style={{
                    "backgroundColor": palette.overlayCardBackgroundStrong,
                    "color":           palette.overlayTextSecondary,
                    "borderColor":     palette.overlayBorder,
                }}
                title="Zoom in (dev only)"
                type="button"
            >
                +
            </button>
            <button
                aria-label="Zoom out (dev only)"
                className="rounded-full px-2 py-1 text-[10px] border opacity-45 hover:opacity-80 active:opacity-90 transition-opacity"
                onClick={onZoomOut}
                style={{
                    "backgroundColor": palette.overlayCardBackgroundStrong,
                    "color":           palette.overlayTextSecondary,
                    "borderColor":     palette.overlayBorder,
                }}
                title="Zoom out (dev only)"
                type="button"
            >
                -
            </button>
        </div>
    );
};

export default ZoomDebugButtons;
